import React, { useContext, createRef, useState, useEffect, useRef } from 'react';

import { CloseIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { Button } from '$partials/ui';
import { Carousel, CarouselContext, CarouselNavigation, CarouselSlide, CarouselWrapper } from '$partials/base/carousel';

import { useBreakpoint, useViewport } from '$hooks/events';
import { Image } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/partials/modals.d.ts').GalleryModal}
 */
const Pagination = ({ images }) => {
    const { swiper } = useContext(CarouselContext);
    const [ elRefs, setElRefs ] = useState([]);

    useEffect(() => {
        setElRefs((elRefs) =>
            Array(images.length)
                .fill()
                .map((_, i) => elRefs[i] || createRef()),
        );
    }, [images.length]);

    useEffect(() => {
        elRefs[swiper?.realIndex]?.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, [swiper?.realIndex]);

    if (!swiper) { return null; }
    return (
        <div
            className='flex pb3 mb3 md-mb5 md-pb5 mt6 width-100 z6 overflow-x-scroll overflow-y-hidden modal-gallery-pagination'>
            {images?.map((item, i) => {
                return (
                    <CarouselSlide
                        className='cursor-pointer mr3'
                        index={i}
                        key={`gallery-modal-pagination-${item?.image?.id}`}
                        onClick={() => {
                            swiper?.slideTo(i);
                            elRefs[i]?.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
                        }}
                        ref={elRefs[i]}>
                        <div
                            className='mb3'
                            style={{height: '70px'}}>
                            <img
                                className='object-contain height-100'
                                src={item?.image?.gallerysrc || item?.image?.src}/>
                        </div>
                        {swiper?.realIndex === i && <hr className='black m0'/>}
                    </CarouselSlide>
                );
            })}
        </div>
    );
};

/**
 * @type {import('$types/components/partials/modals.d.ts').GalleryModal}
 */
const GalleryModal = ({
    images
}) => {
    const { handleModal } = useContext(ModalContext);
    const breakpoint = useBreakpoint([ 'main', 'sm', 'md' ], 'main');
    const { height } = useViewport();
    const maxHeight = height - 215;

    const [videoHeight, setVideoHeight] = useState(undefined);
    const [videoWidth, setVideoWidth] = useState(undefined);
    const videoRef = useRef();

    const handleVideoLoad = () => {
        setVideoHeight(videoRef.current?.videoHeight);
        setVideoWidth(videoRef.current?.videoWidth);
    };

    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            video.addEventListener('loadedmetadata', handleVideoLoad, { passive: true });

            window.setTimeout(() => {
                handleVideoLoad();
            }, 100);
        }

        return () => {
            if (video) {
                video.removeEventListener('loadedmetadata', handleVideoLoad, { passive: true });
            }
        };
    }, []);

    return (
        <Carousel swiperConfig={{
            slidesPerView: 1,
            spaceBetween: 0,
            breakpoints: {
                640: {
                    slidesPerView: 1
                },
                1024: {
                    slidesPerView: 1
                }
            },
        }}>
            <div className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-grey z3'}>
                <div
                    className='flex flex-row-reverse width-100 justify-between absolute top-0 px5 mt5 md-flex-col md-width-0 md-height-100 right right-0 md-mt0 md-px0 md-mr6 z5'>
                    <div className='md-flex justify-end'>
                        <Button
                            aria-label='Close'
                            as = 'button'
                            className = 'bg-white py2 px3 md-px5 md-py4 md-mt6 black uppercase type-style-5 items-center'
                            look = 'button'
                            onClick={() => {
                                handleModal();
                            }}
                            renderIcon = {<CloseIcon className='ml3'/>}>
                            Close
                        </Button>
                    </div>
                    <div className='flex flex-col mt-auto md-mb6 md-absolute-center-y right right-0'>
                        <CarouselNavigation look={breakpoint === 'md' ? 'col' : 'row'} />
                    </div>
                </div>
                <CarouselWrapper
                    className='swiper-wrapper flex flex-col'>
                    {images?.map((item, i) => {
                        const mediaWidth = item?.video ? videoWidth : item.image?.width;
                        const mediaHeight = item?.video ? videoHeight : item.image?.height;
                        return (
                            <CarouselSlide
                                className='flex justify-around mt11 md-mt6'
                                index={i}
                                key={`gallery-modal-${item?.image?.id}`}>
                                <div
                                    className='flex flex-col'
                                    style={{
                                        fontSize: breakpoint === 'md' ? maxHeight : breakpoint === 'sm' ? '767px': '441px',
                                        margin: breakpoint === 'main' && mediaWidth < mediaHeight ? 'auto' : ''}}>
                                    <div style={{
                                        width: `${(mediaWidth / mediaHeight)}em`,
                                        maxWidth: breakpoint === 'md' ? 'calc(100vw - 226px)' : '100vw',
                                        margin: mediaWidth > mediaHeight ? 'auto' : ''
                                    }}>
                                        {item?.video ? (
                                            <video
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                ref={videoRef}
                                                src={item.video.videoUrl}/>
                                        ) : (
                                            <Image {
                                                ...{
                                                    ...item.image,
                                                    ...{
                                                        psrc: item.image.gallerypsrc || item.image.psrc,
                                                        px2: item.image.gallerypx2 || item.image.px2,
                                                        src: item.image.gallerysrc || item.image.src,
                                                        x2: item.image.galleryx2 || item.image.x2,
                                                    }
                                                }
                                            } />
                                        )}
                                        <figcaption style={{
                                            maxWidth: breakpoint === 'md' ? 'calc(100vw - 226px)' : 'calc(100vw - 4rem)',
                                            margin: '0 auto'
                                        }}>
                                            <div className='flex justify-between type-style-7 mt3'>
                                                <div
                                                    className='rich-text'
                                                    dangerouslySetInnerHTML={{__html: item.caption}} />
                                                <p>{i + 1} / {images.length}</p>
                                            </div>
                                        </figcaption>
                                    </div>
                                </div>
                            </CarouselSlide>
                        );
                    })}
                    <Pagination images={images}/>
                </CarouselWrapper>
            </div>
        </Carousel>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default GalleryModal ;
